import type { NotificationCategory, NotificationRecord } from '../types/schema'
import { getNlsString } from '../nls/nlsRuntime'
import type { NlsKey } from '../nls/strings_nls'
import { isPeriodKey, parseChecklistDeepLink } from './checklistDeepLinkService'
import {
  openWorkspaceTarget,
  parseWorkspaceDeepLink,
  type OpenWorkspaceTargetArgs,
} from './workspaceSwitch/openWorkspaceTarget'

export type NavAction = () => void

export type ResolveResult =
  | { ok: true; label: string; action: NavAction }
  | { ok: false; reason: 'unknown-event' | 'missing-entity' | 'stale-deeplink' | 'digest-expand' }

export interface NotificationRouterCtx {
  workspaceSlug: string
  workspaces?: Array<{ id: string; slug?: string; name?: string }>
  openIncomingInvites: () => void
  openMemberSettings: (opts?: { startTab?: string }) => void
  openProfile: () => void
  openOverview: () => void
  openBoardNode: (nodeId: string, boardKey?: string) => void
  openChecklistItem: (itemId: string, periodKey?: string) => void
  openChatThread: (threadId: string) => void
  openMeeting: (meetingId: string) => void
  openForm: (formId: string) => void
  openRaid: (raidId: string) => void
  openAchievement: (achievementId: string) => void
  openCreditsLedger: () => void
  openReport: (reportId: string) => void
  openAiHistory: (jobId?: string) => void
  goto: (url: string) => void
  openStockItem?: (itemId: string) => void
  openTeam?: () => void
  switchToWorkspaceForTarget?: (
    workspaceId: string | null | undefined,
    open: () => void | Promise<void>,
    workspaceName?: string | null,
  ) => Promise<boolean | void> | boolean | void
  openWorkspaceTarget?: (args: OpenWorkspaceTargetArgs) => Promise<boolean | void> | boolean | void
  t?: (key: NlsKey, values?: Record<string, string | number>) => string
}

export type DigestInnerItem = {
  title: string
  body: string
  deepLink?: string | null
  workspaceDeepLink?: string | null
  category?: NotificationCategory | null
  workspaceId?: string | null
}

export type NotificationRouterHandle = {
  resolveNotification: (record: NotificationRecord) => ResolveResult
  resolveDigestItem: (item: DigestInnerItem) => ResolveResult
}

const tr = (ctx: NotificationRouterCtx, key: NlsKey, fallback: string): string => {
  if (ctx.t) {
    try {
      return ctx.t(key)
    } catch {
      // fallback
    }
  }
  try {
    return getNlsString(key)
  } catch {
    return fallback
  }
}

const withEntity = (
  notification: NotificationRecord,
  label: string,
  action: (entityId: string) => void,
): ResolveResult => {
  const entityId = notification.entityId?.trim()
  if (!entityId) return { ok: false, reason: 'missing-entity' }
  return { ok: true, label, action: () => action(entityId) }
}

const extractPeriodKey = (notification: NotificationRecord): string | undefined => {
  const rawPeriod = typeof notification.data?.periodKey === 'string' ? notification.data.periodKey : undefined
  return isPeriodKey(rawPeriod) ? rawPeriod : undefined
}

const dispatchChecklistOpen = (
  ctx: NotificationRouterCtx,
  itemId: string,
  notification: NotificationRecord,
) => {
  const periodKey = extractPeriodKey(notification)
  if (periodKey) {
    ctx.openChecklistItem(itemId, periodKey)
  } else {
    ctx.openChecklistItem(itemId)
  }
}

const resolveChecklistOrBoardNode = (
  notification: NotificationRecord,
  ctx: NotificationRouterCtx,
): ResolveResult =>
  notification.entityType === 'CHECKLIST_ITEM'
    ? withEntity(
        notification,
        tr(ctx, 'NLSNotificationActionOpenChecklistItem', 'Open checklist item'),
        (id) => dispatchChecklistOpen(ctx, id, notification),
      )
    : withEntity(
        notification,
        tr(ctx, 'NLSNotificationActionOpenBoardNode', 'Open board node'),
        (id) => ctx.openBoardNode(id, typeof notification.data?.boardKey === 'string' ? notification.data.boardKey : undefined),
      )

const isBoardEntity = (entityType: NotificationRecord['entityType']) =>
  entityType === 'GOAL' || entityType === 'TASK' || entityType === 'SUBTASK'

const isInternalPath = (value: string) => value.startsWith('/') && !value.startsWith('//')
const isActionableDeepLink = (value: string | null | undefined): value is string => {
  const trimmed = value?.trim()
  return Boolean(trimmed && trimmed.replace(/[\\/#]/g, ''))
}

export const resolveDigestItem = (item: DigestInnerItem, ctx: NotificationRouterCtx): ResolveResult => {
  const deepLink = (item.workspaceDeepLink || item.deepLink)?.trim()
  const parsedLink = parseWorkspaceDeepLink(deepLink)
  const effectiveWorkspaceId = item.workspaceId || parsedLink?.workspaceId || null
  const target = parsedLink?.target

  const wrapDigest = (res: ResolveResult): ResolveResult => {
    if (!res.ok) return res
    if (!effectiveWorkspaceId) return res
    const baseAction = res.action
    return {
      ...res,
      action: () => {
        if (ctx.openWorkspaceTarget) {
          return ctx.openWorkspaceTarget({
            workspaceId: effectiveWorkspaceId,
            target,
            source: 'NOTIFICATION',
            open: baseAction,
          })
        }
        if (ctx.switchToWorkspaceForTarget) {
          return ctx.switchToWorkspaceForTarget(effectiveWorkspaceId, baseAction)
        }
        return openWorkspaceTarget({
          workspaceId: effectiveWorkspaceId,
          target,
          source: 'NOTIFICATION',
          open: baseAction,
        })
      },
    }
  }

  if (isActionableDeepLink(deepLink)) {
    if (!isInternalPath(deepLink)) return { ok: false, reason: 'stale-deeplink' }
    return wrapDigest({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenLink', 'Open link'), action: () => ctx.goto(deepLink) })
  }
  switch (item.category) {
    case 'MENTIONS':
    case 'GROUP_CHAT':
    case 'DUE':
      return wrapDigest({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenOverview', 'Open overview'), action: ctx.openOverview })
    case 'AI_FAILS':
    case 'AI_JOBS':
    case 'INGESTION':
      return wrapDigest({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenAiHistory', 'Open AI history'), action: () => ctx.openAiHistory() })
    case 'GAMIFICATION':
      return wrapDigest({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenProfile', 'Open profile'), action: ctx.openProfile })
    case 'SUBSCRIPTION':
    case 'SECURITY':
    case 'TIPS':
    case 'UPDATES':
    case 'DIGEST':
      return wrapDigest({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenOverview', 'Open overview'), action: ctx.openOverview })
    default:
      return { ok: false, reason: 'unknown-event' }
  }
}

const resolveDigest = (notification: NotificationRecord, ctx: NotificationRouterCtx): ResolveResult => {
  if ((notification.digestCount ?? 1) > 1) return { ok: false, reason: 'digest-expand' }
  const item = notification.digestItems?.[0]
  if (!item) return { ok: false, reason: 'unknown-event' }
  const deepLink = (item.workspaceDeepLink || item.deepLink)?.trim()
  if (isActionableDeepLink(deepLink)) {
    if (!isInternalPath(deepLink)) return { ok: false, reason: 'stale-deeplink' }
    return { ok: true, label: tr(ctx, 'NLSNotificationActionOpenLink', 'Open link'), action: () => ctx.goto(deepLink) }
  }

  switch (item.category) {
    case 'MENTIONS':
    case 'GROUP_CHAT':
      return { ok: false, reason: 'missing-entity' }
    case 'DUE':
      return { ok: false, reason: 'missing-entity' }
    case 'GAMIFICATION':
      return { ok: true, label: tr(ctx, 'NLSNotificationActionOpenProfile', 'Open profile'), action: ctx.openProfile }
    case 'AI_FAILS':
    case 'AI_JOBS':
    case 'INGESTION':
      return { ok: true, label: tr(ctx, 'NLSNotificationActionOpenAiHistory', 'Open AI history'), action: () => ctx.openAiHistory() }
    case 'SUBSCRIPTION':
      return { ok: true, label: tr(ctx, 'NLSNotificationActionOpenCredits', 'Open credits'), action: ctx.openCreditsLedger }
    case 'SECURITY':
    case 'TIPS':
    case 'UPDATES':
    case 'DIGEST':
      return { ok: true, label: tr(ctx, 'NLSNotificationActionOpenOverview', 'Open overview'), action: ctx.openOverview }
    default:
      return { ok: false, reason: 'unknown-event' }
  }
}

const isInviteOrJoinLink = (link?: unknown): boolean => {
  if (typeof link !== 'string') return false
  const trimmed = link.trim()
  return trimmed.includes('/invite-review/') || trimmed.includes('/join')
}

export const resolveNotification = (
  notification: NotificationRecord,
  ctx: NotificationRouterCtx,
): ResolveResult => {
  const deepLink = notification.workspaceDeepLink || notification.deepLink || notification.deeplink
  const parsedLink = parseWorkspaceDeepLink(deepLink)
  const effectiveWorkspaceId = notification.workspaceId || parsedLink?.workspaceId || null
  const target = parsedLink?.target

  const isInvite =
    notification.eventType === 'INVITE_RECEIVED' ||
    notification.entityType === 'INVITE' ||
    isInviteOrJoinLink(deepLink) ||
    isInviteOrJoinLink(notification.data?.deepLink) ||
    isInviteOrJoinLink(notification.data?.deeplink)

  const targetSlug = (() => {
    if (effectiveWorkspaceId && ctx.workspaces) {
      const match = ctx.workspaces.find((w) => w.id === effectiveWorkspaceId)
      if (match?.slug) return match.slug
    }
    return ctx.workspaceSlug
  })()

  const wrap = (res: ResolveResult): ResolveResult => {
    if (!res.ok) return res
    if (!effectiveWorkspaceId || isInvite) return res
    if (!ctx.openWorkspaceTarget && !ctx.switchToWorkspaceForTarget) return res
    const baseAction = res.action
    return {
      ...res,
      action: () => {
        if (ctx.openWorkspaceTarget) {
          return ctx.openWorkspaceTarget({
            workspaceId: effectiveWorkspaceId,
            target,
            source: 'NOTIFICATION',
            open: baseAction,
          })
        }
        return ctx.switchToWorkspaceForTarget!(
          effectiveWorkspaceId,
          baseAction,
          notification.workspaceName,
        )
      },
    }
  }

  const isStockEvent =
    notification.eventType === 'STOCK_LOW' ||
    notification.eventType === 'STOCK_EXPIRING' ||
    notification.eventType === 'STOCK_TRANSFER_ARRIVED' ||
    notification.eventType === 'STOCK_SUGGESTION_WAITING'

  if (isStockEvent) {
    const itemId =
      (typeof notification.data?.itemId === 'string' && notification.data.itemId.trim()) ||
      (typeof notification.data?.stockItemId === 'string' && notification.data.stockItemId.trim()) ||
      (notification.entityType === 'STOCK' || notification.entityType === 'STOCK_ITEM' ? notification.entityId?.trim() : '') ||
      (notification.entityId?.trim() ?? '')
    if (itemId && ctx.openStockItem) {
      return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenStockItem', 'Open stock item'), action: () => ctx.openStockItem!(itemId) })
    }
    const rawLink =
      (typeof notification.workspaceDeepLink === 'string' && notification.workspaceDeepLink.trim()) ||
      (typeof notification.deepLink === 'string' && notification.deepLink.trim()) ||
      (typeof notification.deeplink === 'string' && notification.deeplink.trim()) ||
      (typeof notification.data?.workspaceDeepLink === 'string' && notification.data.workspaceDeepLink.trim()) ||
      (typeof notification.data?.deepLink === 'string' && notification.data.deepLink.trim()) ||
      (typeof notification.data?.deeplink === 'string' && notification.data.deeplink.trim()) ||
      `/console/workspace/${targetSlug}/stock`
    if (isActionableDeepLink(rawLink) && isInternalPath(rawLink)) {
      return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenStockHub', 'Open stock hub'), action: () => ctx.goto(rawLink) })
    }
    return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenStockHub', 'Open stock hub'), action: () => ctx.goto(`/console/workspace/${targetSlug}/stock`) })
  }

  if (typeof deepLink === 'string' && isActionableDeepLink(deepLink)) {
    const parsedChecklistLink = parseChecklistDeepLink(deepLink)
    if (parsedChecklistLink || notification.entityType === 'CHECKLIST_ITEM') {
      const itemId = parsedChecklistLink?.itemId || notification.entityId?.trim()
      if (itemId) {
        const periodKey = parsedChecklistLink?.periodKey || extractPeriodKey(notification)
        return wrap({
          ok: true,
          label: tr(ctx, 'NLSNotificationActionOpenChecklistItem', 'Open checklist item'),
          action: () => (periodKey ? ctx.openChecklistItem(itemId, periodKey) : ctx.openChecklistItem(itemId)),
        })
      }
    }

    const trimmed = deepLink.trim()
    if (!isInternalPath(trimmed)) return { ok: false, reason: 'stale-deeplink' }
    return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenLink', 'Open link'), action: () => ctx.goto(trimmed) })
  }

  switch (notification.eventType) {
    case 'MENTION_CREATED': {
      const threadId = typeof notification.data?.threadId === 'string' ? notification.data.threadId.trim() : ''
      if (threadId) return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenChatThread', 'Open chat thread'), action: () => ctx.openChatThread(threadId) })
      return wrap(resolveChecklistOrBoardNode(notification, ctx))
    }
    case 'ASSIGNMENT_CREATED':
    case 'ASSIGNMENT_CHANGED':
      return wrap(resolveChecklistOrBoardNode(notification, ctx))
    case 'DUE_SOON':
    case 'DUE_TODAY':
    case 'OVERDUE':
    case 'BLOCKED':
    case 'UNBLOCKED':
    case 'RELOCKED':
    case 'WORKFLOW_STATUS_CHANGED':
    case 'PROOF_SUBMITTED':
    case 'PROOF_APPROVED':
    case 'PROOF_REJECTED':
    case 'EXPENSE_APPROVED':
    case 'EXPENSE_REJECTED':
    case 'WINDOW_ENDING':
      return wrap(resolveChecklistOrBoardNode(notification, ctx))
    case 'EDU_ANNOUNCEMENT_WHATSAPP':
    case 'CHECKLIST_SUMMARY':
      return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenOverview', 'Open overview'), action: ctx.openOverview })
    case 'COMPLETION_REVIEW_REQUIRED':
      return wrap(withEntity(notification, tr(ctx, 'NLSNotificationActionOpenChecklistItem', 'Open checklist item'), (id) => dispatchChecklistOpen(ctx, id, notification)))
    case 'RAID_STARTED':
    case 'RAID_ENRAGE_WARNING':
    case 'RAID_COMPLETED':
    case 'RAID_FAILED':
      return wrap(withEntity(notification, tr(ctx, 'NLSNotificationActionOpenRaid', 'Open raid'), ctx.openRaid))
    case 'CREDITS_LOW':
    case 'CREDITS_SPENT':
    case 'CREDITS_REFUNDED':
    case 'AD_LOCK_READY':
      return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenCredits', 'Open credits'), action: ctx.openCreditsLedger })
    case 'STREAK_WARNING':
      return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenProfile', 'Open profile'), action: ctx.openProfile })
    case 'ACHIEVEMENT_UNLOCKED':
    case 'COSMETIC_UNLOCKED':
      return wrap(withEntity(notification, tr(ctx, 'NLSNotificationActionOpenAchievement', 'Open achievement'), ctx.openAchievement))
    case 'INVITE_RECEIVED':
      return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenIncomingInvites', 'Open incoming invites'), action: ctx.openIncomingInvites })
    case 'MEMBER_ROLE_CHANGED':
    case 'BOARD_ROLE_CHANGED':
    case 'PERMISSION_CHANGED':
      return wrap({
        ok: true,
        label: tr(ctx, 'NLSNotificationActionOpenTeam', 'Open Team'),
        action: () => {
          if (ctx.openTeam) {
            ctx.openTeam()
          } else {
            ctx.goto('/team')
          }
        },
      })
    case 'REPORT_READY':
    case 'REPORT_FAILED':
      return wrap(withEntity(notification, tr(ctx, 'NLSNotificationActionOpenReport', 'Open report'), ctx.openReport))
    case 'SAVE_FAILED':
      return wrap(isBoardEntity(notification.entityType)
        ? withEntity(notification, tr(ctx, 'NLSNotificationActionOpenBoardNode', 'Open board node'), (id) => ctx.openBoardNode(id, typeof notification.data?.boardKey === 'string' ? notification.data.boardKey : undefined))
        : { ok: true, label: tr(ctx, 'NLSNotificationActionOpenMemberSettings', 'Open member settings'), action: () => ctx.openMemberSettings({ startTab: 'SYNC' }) })
    case 'SYSTEM_NOTICE':
      return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenOverview', 'Open overview'), action: ctx.openOverview })
    case 'FORM_REQUESTED':
      return wrap(withEntity(notification, tr(ctx, 'NLSNotificationActionOpenForm', 'Open form'), ctx.openForm))
    case 'STOCK_INSUFFICIENT':
    case 'BUDGET_OVER_LIMIT': {
      if (notification.entityType === 'CHECKLIST_ITEM') return wrap(withEntity(notification, tr(ctx, 'NLSNotificationActionOpenChecklistItem', 'Open checklist item'), (id) => dispatchChecklistOpen(ctx, id, notification)))
      const formId = typeof notification.data?.formId === 'string' ? notification.data.formId.trim() : ''
      if (notification.entityType === 'FORM_RESPONSE' && formId) return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenForm', 'Open form'), action: () => ctx.openForm(formId) })
      return { ok: false, reason: 'missing-entity' }
    }
    case 'CLIENT_QUOTE_DECIDED':
      return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenClients', 'Open clients'), action: () => ctx.goto(`/console/workspace/${targetSlug}/clients`) })
    case 'DIGEST':
      return resolveDigest(notification, ctx)
    default:
      break
  }

  const entityId = notification.entityId?.trim()
  if (!entityId) return { ok: false, reason: 'unknown-event' }
  if (notification.entityType === 'QUOTE') return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenClients', 'Open clients'), action: () => ctx.goto(`/console/workspace/${targetSlug}/clients`) })
  if (notification.entityType === 'RAID') return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenRaid', 'Open raid'), action: () => ctx.openRaid(entityId) })
  if (notification.entityType === 'CHECKLIST_ITEM') return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenChecklistItem', 'Open checklist item'), action: () => dispatchChecklistOpen(ctx, entityId, notification) })
  if (notification.entityType === 'REPORT') return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenReport', 'Open report'), action: () => ctx.openReport(entityId) })
  if (notification.entityType === 'ACHIEVEMENT') return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenAchievement', 'Open achievement'), action: () => ctx.openAchievement(entityId) })
  if (notification.entityType === 'FORM') return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenForm', 'Open form'), action: () => ctx.openForm(entityId) })
  if (isBoardEntity(notification.entityType)) return wrap({ ok: true, label: tr(ctx, 'NLSNotificationActionOpenBoardNode', 'Open board node'), action: () => ctx.openBoardNode(entityId) })
  return { ok: false, reason: 'unknown-event' }
}
